import React, { useEffect, useState, useCallback } from 'react';
import { UserPlus, KeyRound, ShieldCheck, ShieldOff, RefreshCw, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  apiListUsers,
  apiCreateUser,
  apiUpdateUser,
  apiResetUserPassword,
  apiErrorMessage,
} from '../config/api';

const roleBadge = (role) =>
  role === 'admin'
    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';

function AddUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'viewer', password: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const user = await apiCreateUser({
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        password: form.password || undefined,
      });
      onCreated(user);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create user'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Add user</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Analyst"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="jane@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-900"
            >
              <option value="viewer">Viewer — dashboard access only</option>
              <option value="admin">Admin — can manage users</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">
              Temporary password <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Leave blank for Google-only sign-in"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
            <p className="text-xs text-gray-400 mt-1">
              If set, must be 8+ characters. Otherwise the user signs in with Google.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const UserManagement = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers(await apiListUsers());
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 3000);
  };

  const patch = async (u, changes, successMsg) => {
    setBusyId(u.id);
    setError('');
    try {
      const updated = await apiUpdateUser(u.id, changes);
      setUsers((list) => list.map((x) => (x.id === u.id ? updated : x)));
      if (successMsg) flash(successMsg);
    } catch (err) {
      setError(apiErrorMessage(err, 'Update failed'));
    } finally {
      setBusyId(null);
    }
  };

  const changeRole = (u, role) => {
    if (role === u.role) return;
    patch(u, { role }, `${u.email} is now ${role}`);
  };

  const toggleActive = (u) => patch(
    u,
    { isActive: !u.isActive },
    u.isActive ? `${u.email} deactivated` : `${u.email} reactivated`
  );

  const resetPassword = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email} (min 8 chars):`);
    if (pw == null) return;
    if (pw.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setBusyId(u.id);
    setError('');
    try {
      await apiResetUserPassword(u.id, pw);
      flash(`Password updated for ${u.email}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Reset failed'));
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-light text-gray-900">User management</h1>
          <p className="text-sm text-gray-500 mt-1">Add teammates, set roles, and control access.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded"
          >
            <UserPlus className="w-4 h-4" /> Add user
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{error}</div>}
      {notice && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{notice}</div>}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Sign-in</th>
                <th className="px-4 py-3 font-semibold">Last login</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">Loading users…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-10 text-center text-gray-400">No users yet.</td></tr>
              ) : (
                users.map((u) => {
                  const isMe = me && String(me.id) === String(u.id);
                  const busy = busyId === u.id;
                  return (
                    <tr key={u.id} className={`border-t border-gray-100 ${!u.isActive ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {u.name || u.email.split('@')[0]}
                          {isMe && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                        </div>
                        <div className="text-gray-500 text-xs">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          disabled={busy || isMe}
                          onChange={(e) => changeRole(u, e.target.value)}
                          title={isMe ? "You can't change your own role" : ''}
                          className={`text-xs border rounded px-2 py-1 bg-white disabled:cursor-not-allowed ${roleBadge(u.role)}`}
                        >
                          <option value="viewer">viewer</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                            u.isActive
                              ? 'bg-green-50 text-green-700 border-green-200'
                              : 'bg-gray-100 text-gray-500 border-gray-200'
                          }`}
                        >
                          {u.isActive ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs capitalize">{u.provider || 'local'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(u.lastLoginAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => resetPassword(u)}
                            disabled={busy}
                            title="Reset password"
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-40"
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => toggleActive(u)}
                            disabled={busy || isMe}
                            title={isMe ? "You can't disable yourself" : u.isActive ? 'Deactivate' : 'Reactivate'}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {u.isActive ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <AddUserModal
          onClose={() => setShowAdd(false)}
          onCreated={(user) => {
            setUsers((list) => [user, ...list]);
            setShowAdd(false);
            flash(`${user.email} added`);
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
